import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, PencilSimple, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EMPTY = { driver_name: "", email_address: "", phone_number: "", status: "Active" };

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/drivers");
      setDrivers(res.data);
    } catch {
      toast.error("Failed to load drivers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (driver) => {
    setEditing(driver);
    setForm({
      driver_name: driver.driver_name,
      email_address: driver.email_address,
      phone_number: driver.phone_number,
      status: driver.status,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.driver_name.trim() || !form.email_address.trim() || !form.phone_number.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/drivers/${editing.id}`, form);
        toast.success("Driver updated");
      } else {
        await api.post("/drivers", form);
        toast.success("Driver added");
      }
      setOpen(false);
      await load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (driver) => {
    const next = driver.status === "Active" ? "Inactive" : "Active";
    try {
      await api.put(`/drivers/${driver.id}`, { status: next });
      toast.success(`${driver.driver_name} is now ${next}`);
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="drivers-page">
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900">Drivers</h1>
          <p className="mt-1.5 text-[15px] text-gray-600">
            Active drivers receive new order notifications by email.
          </p>
        </div>
        <button
          data-testid="add-driver-button"
          onClick={openCreate}
          className="inline-flex h-11 items-center gap-2 rounded-md bg-[#1A1A1A] px-5 text-sm font-semibold text-white hover:bg-[#4D4D4D]"
        >
          <Plus size={16} weight="bold" /> Add Driver
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-[15px] text-gray-500">
            <Spinner size={18} className="mr-2 animate-spin" /> Loading drivers…
          </div>
        ) : drivers.length === 0 ? (
          <div className="py-16 text-center text-[15px] text-gray-500">
            No drivers yet. Click <strong>Add Driver</strong> to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="drivers-table" className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-[#f3f4f6] text-left text-sm text-gray-600">
                  <th className="px-5 py-3 font-semibold">Driver Name</th>
                  <th className="px-5 py-3 font-semibold">Email Address</th>
                  <th className="px-5 py-3 font-semibold">Phone Number</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <tr
                    key={d.id}
                    data-testid={`driver-row-${d.id}`}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="px-5 py-4 text-[15px] font-medium text-gray-900">{d.driver_name}</td>
                    <td className="px-5 py-4">
                      <a href={`mailto:${d.email_address}`} className="text-[15px] text-[#1A1A1A] hover:underline">
                        {d.email_address}
                      </a>
                    </td>
                    <td className="px-5 py-4 font-mono text-[15px] text-gray-700">{d.phone_number}</td>
                    <td className="px-5 py-4">
                      <StatusBadge status={d.status} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`edit-driver-${d.id}`}
                          onClick={() => openEdit(d)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          <PencilSimple size={14} weight="bold" /> Edit
                        </button>
                        <button
                          data-testid={`toggle-status-${d.id}`}
                          onClick={() => toggleStatus(d)}
                          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {d.status === "Active" ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="driver-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Driver" : "Add Driver"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Update driver details. Email is used for order notifications."
                : "New drivers receive order notifications at their email address."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Field label="Driver Name">
              <input
                data-testid="driver-form-name"
                value={form.driver_name}
                onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
                placeholder="e.g. Wes Brown"
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
                autoFocus
              />
            </Field>
            <Field label="Email Address">
              <input
                data-testid="driver-form-email"
                type="email"
                value={form.email_address}
                onChange={(e) => setForm({ ...form, email_address: e.target.value })}
                placeholder="driver@coolmanfuels.ca"
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
              />
            </Field>
            <Field label="Phone Number">
              <input
                data-testid="driver-form-phone"
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="(705) 555-0000"
                className="h-10 w-full rounded-md border border-gray-300 px-3 text-[15px] focus:border-[#1A1A1A] focus:outline-none focus:ring-1 focus:ring-[#1A1A1A]"
              />
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="driver-form-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <DialogFooter>
            <button
              data-testid="driver-form-cancel"
              onClick={() => setOpen(false)}
              disabled={saving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              data-testid="driver-form-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-[#1A1A1A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#4D4D4D] disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Spinner size={14} className="animate-spin" /> Saving…
                </>
              ) : editing ? (
                "Save Changes"
              ) : (
                "Add Driver"
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-gray-700">{label}</label>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles =
    status === "Active"
      ? "bg-green-50 text-green-800 border-green-300"
      : "bg-gray-100 text-gray-600 border-gray-300";
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-sm font-medium ${styles}`}>
      {status}
    </span>
  );
}
